import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../../api/client.js';
import { useApiQuery } from '../../../api/useApiQuery.js';
import { CategoryEditor } from './CategoryEditor.jsx';
import { moveId } from './reorderHelpers.js';
import { MenuQrCode } from '../../../components/MenuQrCode.jsx';
import { getPublicMenuUrl } from '../../../utils/publicMenuUrl.js';

function Icon({ name, size = 18 }) {
  const paths = {
    back: <path d="m15 18-6-6 6-6" />,
    eye: <><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" /><circle cx="12" cy="12" r="2.5" /></>,
    palette: <><circle cx="13.5" cy="6.5" r="1" /><circle cx="17.5" cy="10.5" r="1" /><circle cx="8.5" cy="7.5" r="1" /><path d="M12 3a9 9 0 1 0 0 18h1.5a2 2 0 0 0 0-4H12a2 2 0 0 1 0-4h3a6 6 0 0 0 0-12Z" /></>,
    history: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5M12 7v5l3 2" /></>,
    publish: <><path d="M12 16V4M7 9l5-5 5 5" /><path d="M5 14v5h14v-5" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    check: <path d="m5 12 4 4L19 6" />,
    menu: <path d="M5 5h14M5 12h14M5 19h14" />,
    help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.6 2.6 0 1 1 3.6 2.4c-.8.4-1.1.8-1.1 1.6M12 17h.01" /></>,
  };

  return <svg className="ui-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

// The editor and customer preview share the same draft-tree endpoint. Every
// change re-fetches that tree so the paper canvas always reflects saved data.
export function MenuEditorPage() {
  const { menuId } = useParams();
  const { data: tree, loading, error, reload } = useApiQuery(() => api.get(`/api/menus/${menuId}/publish/preview`), [menuId]);
  const [addingCategory, setAddingCategory] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState(null);
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [reorderingCategory, setReorderingCategory] = useState(false);

  async function handleAddCategory(event) {
    event.preventDefault();
    if (!categoryName.trim()) return;
    setCategorySubmitting(true);
    setActionError(null);
    try {
      await api.post(`/api/menus/${menuId}/categories`, { name: categoryName.trim() });
      setCategoryName('');
      setAddingCategory(false);
      reload();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setCategorySubmitting(false);
    }
  }

  async function handleReorderCategory(categoryId, direction) {
    const ids = tree.categories.map((category) => category.id);
    const orderedIds = moveId(ids, categoryId, direction);
    if (orderedIds === ids) return;
    setActionError(null);
    setReorderingCategory(true);
    try {
      await api.post(`/api/menus/${menuId}/categories/reorder`, { orderedIds });
      reload();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setReorderingCategory(false);
    }
  }

  async function handlePublish() {
    setPublishing(true);
    setPublishMessage(null);
    setActionError(null);
    try {
      const result = await api.post(`/api/menus/${menuId}/publish`);
      setPublishMessage({ type: 'success', text: `Version ${result.version} is live. Customers can now see these changes.` });
      reload();
    } catch (err) {
      setPublishMessage({ type: 'error', text: err.message });
    } finally {
      setPublishing(false);
    }
  }

  function beginAddingCategory() {
    setAddingCategory(true);
    window.requestAnimationFrame(() => {
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      document.querySelector('#new-category-name')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    });
  }

  if (loading && !tree) return <div className="menu-builder-loading"><span className="spinner" aria-label="Loading" /><span>Opening your menu…</span></div>;
  if (error) return <p className="field-error">{error.message}</p>;

  const itemCount = tree.categories.reduce((total, category) => total + category.items.length, 0);

  return (
    <div className="menu-builder">
      <header className="menu-builder__header">
        <div className="menu-builder__heading">
          <Link className="menu-builder__back" to="/menus"><Icon name="back" size={16} /> All menus</Link>
          <div className="menu-builder__title-row">
            <div><h1>{tree.menu.name}</h1><p>Edit this page like a printed menu. Your changes save as you go.</p></div>
            <span className="save-status"><Icon name="check" size={14} /> Saved</span>
          </div>
        </div>
        <div className="menu-builder__actions">
          {tree.menu.isPublished ? (
            <MenuQrCode menuName={tree.menu.name} menuSlug={tree.menu.slug} menuUrl={getPublicMenuUrl(tree.tenant.slug, tree.menu.slug)} />
          ) : (
            <span className="qr-unavailable">Publish to get QR code</span>
          )}
          <Link to={`/menus/${menuId}/preview`} className="btn"><Icon name="eye" /> Preview</Link>
          <button type="button" className="btn btn-primary" onClick={handlePublish} disabled={publishing}><Icon name="publish" /> {publishing ? 'Publishing…' : 'Publish menu'}</button>
        </div>
      </header>

      <nav className="menu-builder__tabs" aria-label="Menu settings">
        <span className="menu-builder__tab active"><Icon name="menu" /> Menu content</span>
        <Link className="menu-builder__tab" to={`/menus/${menuId}/appearance`}><Icon name="palette" /> Design & style</Link>
        <Link className="menu-builder__tab" to={`/menus/${menuId}/history`}><Icon name="history" /> Publish history</Link>
      </nav>

      {publishMessage && <p className={publishMessage.type === 'success' ? 'menu-builder__alert' : 'field-error menu-builder__alert'} role={publishMessage.type === 'error' ? 'alert' : 'status'}>{publishMessage.text}</p>}
      {actionError && <p className="field-error menu-builder__alert" role="alert">{actionError}</p>}

      <div className="menu-builder__workspace">
        <aside className="menu-outline" aria-label="Menu outline">
          <div className="menu-outline__label">On this menu</div>
          {tree.categories.length > 0 ? (
            <ol className="menu-outline__list">
              {tree.categories.map((category) => <li key={category.id}><a href={`#category-${category.id}`}><span>{category.name}</span><small>{category.items.length}</small></a></li>)}
            </ol>
          ) : <p className="menu-outline__empty">Your sections will appear here.</p>}
          <button type="button" className="menu-outline__add" onClick={beginAddingCategory}><Icon name="plus" size={16} /> Add section</button>
          <div className="menu-outline__summary"><strong>{tree.categories.length}</strong> {tree.categories.length === 1 ? 'section' : 'sections'} · <strong>{itemCount}</strong> {itemCount === 1 ? 'item' : 'items'}</div>
        </aside>

        <main className="menu-paper-wrap">
          <div className="menu-paper">
            <div className="menu-paper__masthead">
              <span className="menu-paper__eyebrow">{tree.tenant.name}</span>
              <h2>{tree.menu.name}</h2>
              {tree.menu.description && <p>{tree.menu.description}</p>}
            </div>

            {tree.categories.length === 0 && !addingCategory && (
              <div className="menu-paper__welcome">
                <div className="menu-paper__welcome-icon"><Icon name="menu" size={26} /></div>
                <h3>Start with a menu section</h3>
                <p>Most menus begin with sections such as Starters, Main Course, Desserts, or Drinks.</p>
                <button type="button" className="btn btn-primary" onClick={beginAddingCategory}><Icon name="plus" /> Add your first section</button>
              </div>
            )}

            {tree.categories.map((category, index) => (
              <CategoryEditor key={category.id} menuId={menuId} category={category} currency={tree.tenant.currency} isFirst={index === 0} isLast={index === tree.categories.length - 1} onReorderCategory={handleReorderCategory} categoryReordering={reorderingCategory} onChanged={reload} />
            ))}

            {addingCategory ? (
              <form onSubmit={handleAddCategory} className="new-menu-section">
                <label htmlFor="new-category-name">New section name</label>
                <div className="new-menu-section__row">
                  <input id="new-category-name" type="text" required autoFocus placeholder="e.g. Starters, Main Course, Drinks" value={categoryName} onChange={(event) => setCategoryName(event.target.value)} />
                  <button type="submit" className="btn btn-primary" disabled={categorySubmitting}>{categorySubmitting ? 'Adding…' : 'Add section'}</button>
                  <button type="button" className="btn btn-ghost" disabled={categorySubmitting} onClick={() => setAddingCategory(false)}>Cancel</button>
                </div>
                <span>Sections help guests scan your menu quickly.</span>
              </form>
            ) : tree.categories.length > 0 && <button type="button" className="menu-paper__add-section" onClick={beginAddingCategory}><Icon name="plus" /> Add another section</button>}
          </div>
          <div className="menu-builder-tip"><Icon name="help" size={17} /> Tip: Add dishes in the same order you want guests to see them.</div>
        </main>
      </div>
    </div>
  );
}
